import AreaColapsavel from 'components/AreaColapsavel';
import Button from 'components/Form/Button';
import FormCore from 'components/Form/Core';
import Input from 'components/Form/Input';
import { Icon } from 'components/Icon';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Create as CreateAlerta,
  Encerrar as EncerrarAlerta,
  ReadByCobranca,
} from 'services/alerta-cobranca';
import { exposeStrapiError } from 'utils';
import { estiloAlerta } from 'utils/alertas';
import {
  AlertaAcoes,
  AlertaEscopo,
  AlertaLinha,
  AlertaMensagem,
  AlertaMeta,
  AlertasContainer,
  AlertasLista,
  AlertaTexto,
  BarraTopo,
  BotaoMais,
  ExtrasConteudo,
  NovoAlertaContainer,
  ResumoAlertas,
  ResumoLinha,
} from './styled';

const optionsNivel = [
  { id: 'critico', title: 'Crítico' },
  { id: 'atencao', title: 'Atenção' },
  { id: 'info', title: 'Informativo' },
];

export const Alertas = ({ cobrancaId, clienteId, onChange }) => {
  const [alertas, setAlertas] = useState([]);
  const [criticos, setCriticos] = useState(0);
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [criando, setCriando] = useState(false);
  const [encerrando, setEncerrando] = useState(null);
  const [expandido, setExpandido] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [formRegister, setFormRegister] = useState({});
  const formRef = useRef(null);

  const init = useCallback(async () => {
    if (!cobrancaId) {
      return;
    }
    setLoading(true);
    try {
      // Endpoint dedicado: ja devolve contrato + cliente ordenado por gravidade
      const result = await ReadByCobranca(cobrancaId);
      setAlertas(result?.alertas || []);
      setCriticos(result?.criticos || 0);
    } catch (err) {
      console.error('Erro ao carregar alertas:', err);
      toast.error('Erro ao carregar os alertas');
    } finally {
      setLoading(false);
    }
  }, [cobrancaId]);

  useEffect(() => init(), [init]);

  const formNovoAlerta = [
    {
      ref: 'nivel',
      placeholder: 'Nível',
      options: optionsNivel,
      required: true,
      half: true,
    },
    ...(clienteId
      ? [
          {
            ref: 'escopo',
            placeholder: 'Aplicar a',
            options: [
              { id: 'contrato', title: 'Somente este contrato' },
              { id: 'cliente', title: 'Cliente (todos os contratos)' },
            ],
            required: true,
            half: true,
          },
        ]
      : []),
    {
      // O doc recomenda sempre expirar: aviso fixo sem prazo vira ruido
      ref: 'data_expiracao',
      placeholder: 'Expira em (recomendado)',
      type: 'date',
      half: true,
    },
    {
      ref: 'mensagem',
      placeholder: 'Mensagem do alerta',
      type: 'text',
      required: true,
      full: true,
      multiline: true,
      maxRows: 3,
      height: 72,
    },
  ];

  const salvar = async () => {
    const form = formRef.current?.getForm?.();
    if (!form) {
      return;
    }

    setSalvando(true);
    try {
      // cobranca OU cliente, nunca os dois: enviar ambos retorna 400
      const porCliente = form?.escopo === 'cliente' && clienteId;
      const payload = {
        ...(porCliente ? { cliente: clienteId } : { cobranca: cobrancaId }),
        nivel: form?.nivel,
        mensagem: form?.mensagem,
        data_expiracao: form?.data_expiracao || null,
      };

      const result = await CreateAlerta({ data: payload });
      if (exposeStrapiError(result)) {
        return;
      }

      toast.success('Alerta criado com sucesso!');
      setFormRegister({});
      setCriando(false);
      await init();
      onChange?.();
    } catch (err) {
      console.error('Erro ao criar alerta:', err);
      toast.error('Erro ao criar o alerta');
    } finally {
      setSalvando(false);
    }
  };

  const encerrar = async (id) => {
    if (!motivo?.trim()) {
      toast.error('Informe o motivo para encerrar o alerta.');
      return;
    }

    setSalvando(true);
    try {
      const result = await EncerrarAlerta(id, motivo.trim());
      if (exposeStrapiError(result)) {
        return;
      }

      toast.success('Alerta encerrado.');
      setEncerrando(null);
      setMotivo('');
      await init();
      onChange?.();
    } catch (err) {
      console.error('Erro ao encerrar alerta:', err);
      toast.error('Erro ao encerrar o alerta');
    } finally {
      setSalvando(false);
    }
  };

  if (loading && !alertas.length) {
    return null;
  }

  // Colapsado mostra so o mais grave (a lista ja vem ordenada por gravidade).
  // Enquanto ha um encerramento em andamento a lista fica aberta a forca, senao
  // o campo de motivo sumiria junto com a linha.
  const mostrarTodos = expandido || !!encerrando;
  const [primeiro, ...extras] = alertas;

  const renderAlerta = (item) => {
    const estilo = estiloAlerta(item?.nivel);
    return (
      <AlertaLinha key={item?.id} color={estilo.cor} background={estilo.fundo}>
        <Icon name={estilo.icone} size={20} color={estilo.cor} />
        <AlertaTexto>
          <AlertaMensagem>{item?.mensagem}</AlertaMensagem>
          <AlertaMeta>
            {item?.usuario_email || '-'}
            {item?.data
              ? ` · ${moment(item.data).format('DD/MM/YYYY HH:mm')}`
              : ''}
            {item?.data_expiracao
              ? ` · expira em ${moment(item.data_expiracao).format('DD/MM/YYYY')}`
              : ' · sem prazo'}
          </AlertaMeta>
          {encerrando === item?.id ? (
            <AlertaAcoes>
              <Input
                placeholder="Motivo do encerramento"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
              />
              <Button
                color="blue"
                small
                nospace
                width="auto"
                disabled={salvando}
                onClick={() => encerrar(item?.id)}
              >
                Confirmar
              </Button>
              <Button
                color="lightgrey"
                small
                nospace
                width="auto"
                onClick={() => {
                  setEncerrando(null);
                  setMotivo('');
                }}
              >
                Cancelar
              </Button>
            </AlertaAcoes>
          ) : null}
        </AlertaTexto>
        <AlertaEscopo color={estilo.cor}>
          {item?.escopo === 'cliente' ? 'Cliente' : 'Contrato'}
        </AlertaEscopo>
        {encerrando === item?.id ? null : (
          <Icon
            name="check_circle"
            size={20}
            color="#a4a4a4"
            onClick={() => {
              setEncerrando(item?.id);
              setMotivo('');
            }}
            elementAttributes={{
              title: 'Encerrar alerta',
              style: { cursor: 'pointer' },
            }}
          />
        )}
      </AlertaLinha>
    );
  };

  return (
    <AlertasContainer>
      <BarraTopo>
        <ResumoLinha>
          <ResumoAlertas>
            {!alertas.length
              ? 'Nenhum alerta ativo neste contrato.'
              : `${alertas.length} alerta(s) ativo(s)${criticos ? ` · ${criticos} crítico(s)` : ''}`}
          </ResumoAlertas>
          {alertas.length > 1 ? (
            <BotaoMais
              type="button"
              disabled={!!encerrando}
              title={
                encerrando
                  ? 'Conclua ou cancele o encerramento em andamento'
                  : ''
              }
              onClick={() => !encerrando && setExpandido(!expandido)}
            >
              {mostrarTodos
                ? 'ver menos'
                : `+${extras.length} alerta${extras.length > 1 ? 's' : ''}`}
            </BotaoMais>
          ) : null}
        </ResumoLinha>
        <Button
          color={criando ? 'lightgrey' : 'blue'}
          small
          nospace
          width="auto"
          onClick={() => setCriando(!criando)}
        >
          {criando ? 'Cancelar' : '+ Alerta'}
        </Button>
      </BarraTopo>

      <AreaColapsavel aberto={criando} vao="8px">
        <NovoAlertaContainer>
          <FormCore
            ref={formRef}
            formItems={formNovoAlerta}
            register={formRegister}
          />
          <Button
            color="blue"
            small
            nospace
            width="auto"
            disabled={salvando}
            onClick={salvar}
          >
            {salvando ? 'Salvando...' : 'Criar alerta'}
          </Button>
        </NovoAlertaContainer>
      </AreaColapsavel>

      <AlertasLista>
        {primeiro ? renderAlerta(primeiro) : null}
        {extras.length ? (
          <AreaColapsavel aberto={mostrarTodos}>
            <ExtrasConteudo>{extras.map(renderAlerta)}</ExtrasConteudo>
          </AreaColapsavel>
        ) : null}
      </AlertasLista>
    </AlertasContainer>
  );
};
